<script setup>
import { ref, watch, nextTick } from 'vue'
import axios from 'axios'
import { useRoute } from 'vue-router'

const props = defineProps({
  playerId: {
    type: [Number, String],
    required: true
  },
  playerName: {
    type: String,
    required: true
  },
  show: {
    type: Boolean,
    required: true
  },
  fixtureId: {
    type: [Number, String],
    default: null
  },
  playerStatus: {
    type: [Number, String],
    default: null
  },
  rosterItemId: {
    type: [Number, String],
    default: null
  }
})

const emit = defineEmits(['close', 'status-updated'])

const route = useRoute()
const selectedStatus = ref('active')
const playerNews = ref([])
const newNewsMessage = ref('')
const loading = ref(false)
const newsContainer = ref(null)
const messageEditor = ref(null)
const statusOptions = ref([])
const statusIdToName = ref({})
const statusNameToId = ref({})
const isModalOpened = ref(false)
const isAddingNews = ref(false)
const isRead = ref(false)
const togglingRead = ref(false)

// Watch for show prop changes
watch(() => props.show, (newValue) => {
  if (newValue && !isModalOpened.value) {
    console.log('PlayerNewsModal: Show prop changed to true, opening modal')
    isModalOpened.value = true
    openModal()
    // Scroll to bottom when modal opens
    nextTick(() => {
      setTimeout(() => {
        scrollToBottom()
      }, 200)
    })
  } else if (!newValue) {
    // Reset when modal closes
    isModalOpened.value = false
    playerNews.value = []
    loading.value = false
  }
}, { immediate: true })

// Watch for playerId changes to reload news
watch(() => props.playerId, (newPlayerId, oldPlayerId) => {
  if (props.show && newPlayerId && newPlayerId !== oldPlayerId && !isAddingNews.value) {
    console.log('PlayerNewsModal: PlayerId changed, reloading news')
    loadPlayerNews()
  }
})

// Watch for playerStatus changes to sync with row
watch(() => props.playerStatus, (newStatus) => {
  if (newStatus && statusIdToName.value && statusIdToName.value[newStatus]) {
    selectedStatus.value = statusIdToName.value[newStatus]
  }
})

// Watch for changes in playerNews array and scroll to bottom
watch(() => playerNews.value.length, () => {
  nextTick(() => {
    scrollToBottom()
  })
})

async function openModal() {
  console.log('PlayerNewsModal: Opening modal for player:', props.playerId, props.playerName)
  
  // Load status options first
  await loadStatusOptions()
  
  // Set initial status from player data if available
  if (props.playerStatus && statusIdToName.value[props.playerStatus]) {
    selectedStatus.value = statusIdToName.value[props.playerStatus]
  } else {
    selectedStatus.value = 'active'
  }
  
  // Load player news
  await loadPlayerNews()
}

async function loadStatusOptions() {
  try {
    const response = await axios.get('/api/v2/projections-players/player-statuses')
    
    if (response.data && Array.isArray(response.data)) {
      statusOptions.value = response.data.map(item => ({
        id: item.id || item.value,
        name: (item.name || item.label || '').toLowerCase()
      }))
      
      // Create mapping objects
      statusIdToName.value = {}
      statusNameToId.value = {}
      
      statusOptions.value.forEach(option => {
        statusIdToName.value[option.id] = option.name
        statusNameToId.value[option.name] = option.id
      })
    }
  } catch (error) {
    console.error('Error loading status options:', error)
    // Fallback mappings
    statusIdToName.value = { 1: 'active', 2: 'questionable', 3: 'out' }
    statusNameToId.value = { 'active': 1, 'questionable': 2, 'out': 3 }
  }
}

function closeModal() {
  emit('close')
  newNewsMessage.value = ''
  if (messageEditor.value) {
    messageEditor.value.innerHTML = ''
  }
}

function getMessageContent() {
  if (messageEditor.value) {
    // Get text content and preserve basic HTML (links, images, formatting)
    return messageEditor.value.innerHTML.trim()
  }
  return ''
}

function getMessageText() {
  if (messageEditor.value) {
    // Get plain text for validation
    return messageEditor.value.innerText.trim()
  }
  return ''
}

async function loadPlayerNews() {
  if (!props.playerId) {
    console.error('PlayerNewsModal: playerId is missing')
    playerNews.value = []
    loading.value = false
    return
  }
  
  loading.value = true
  console.log('PlayerNewsModal: Loading news for player ID:', props.playerId)
  
  try {
    // Load all news for the player (not filtered by fixture)
    const response = await axios.get(`/api/players/${props.playerId}/news`)
    console.log('PlayerNewsModal: API response:', response.data)
    
    if (response.data?.success && response.data?.news) {
      // News is already in ascending order (oldest first, newest last)
      playerNews.value = response.data.news
      isRead.value = response.data.isRead ?? false
      console.log('PlayerNewsModal: Loaded', playerNews.value.length, 'news items')
    } else if (response.data?.news) {
      // Fallback if success field is not present
      playerNews.value = response.data.news
      isRead.value = response.data.isRead ?? false
      console.log('PlayerNewsModal: Loaded', playerNews.value.length, 'news items (fallback)')
    } else if (Array.isArray(response.data)) {
      // Handle case where response.data is directly an array
      playerNews.value = response.data
      console.log('PlayerNewsModal: Loaded', playerNews.value.length, 'news items (array format)')
    } else {
      console.warn('PlayerNewsModal: Unexpected response format:', response.data)
      playerNews.value = []
    }
    
    // Scroll to bottom after loading - use multiple attempts to ensure it works
    await nextTick()
    scrollToBottom()
    // Try again after a short delay to ensure DOM is fully rendered
    setTimeout(() => {
      scrollToBottom()
    }, 100)
  } catch (error) {
    console.error('PlayerNewsModal: Error loading player news:', error)
    console.error('PlayerNewsModal: Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url
    })
    playerNews.value = []
  } finally {
    loading.value = false
    // Scroll after loading completes
    await nextTick()
    scrollToBottom()
  }
}

async function updatePlayerStatus() {
  // Convert status name to ID
  const statusId = statusNameToId.value[selectedStatus.value]
  if (!statusId) {
    console.error('Invalid status:', selectedStatus.value)
    return
  }
  
  // Prevent reloads while updating status
  isAddingNews.value = true
  
  // Update player status via API (same endpoint as dropdown)
  if (props.rosterItemId) {
    try {
      const actualPlayerId = props.playerId
      const response = await axios.put(`/api/v2/projections-players/teams/player/${props.rosterItemId}/status`, {
        player_status: statusId,
        player_id: actualPlayerId
      })
      
      if (response.data.success) {
        // Emit status-updated event with the status ID
        emit('status-updated', {
          statusId: statusId,
          statusName: selectedStatus.value,
          rosterItemId: props.rosterItemId
        })
      }
    } catch (error) {
      console.error('Error updating player status:', error)
      alert('Failed to update player status: ' + (error.response?.data?.error || error.message))
      isAddingNews.value = false
      return
    }
  }
  
  // Add automatic news message about status change (status is stored in player_news table)
  const statusMessages = {
    'active': 'Player status changed to Active',
    'questionable': 'Player status changed to Questionable',
    'out': 'Player status changed to Out'
  }
  
  const statusMessage = statusMessages[selectedStatus.value] || `Player status changed to ${selectedStatus.value}`
  
  try {
    console.log('PlayerNewsModal: Adding status change news, current count:', playerNews.value.length)
    const newsResponse = await axios.post(`/api/players/${props.playerId}/news`, {
      message: statusMessage,
      status: selectedStatus.value,
      fixtureId: props.fixtureId || route.params.id
    })
    
    if (newsResponse.data?.success && newsResponse.data?.news) {
      // Add the new news to the end of the list (bottom) - don't reload
      const newNewsItem = {
        id: newsResponse.data.news.id,
        player_id: newsResponse.data.news.player_id,
        fixture_id: newsResponse.data.news.fixture_id,
        message: newsResponse.data.news.message,
        status: newsResponse.data.news.status,
        author: newsResponse.data.news.author,
        created_at: newsResponse.data.news.created_at,
        timestamp: newsResponse.data.news.timestamp || newsResponse.data.news.created_at
      }
      playerNews.value.push(newNewsItem)
      isRead.value = false // New message = unread for everyone
      console.log('PlayerNewsModal: Appended status change news, new count:', playerNews.value.length)
      
      // Scroll to bottom after adding status change message
      await nextTick()
      scrollToBottom()
    } else if (newsResponse.data?.news) {
      // Fallback handling
      playerNews.value.push(newsResponse.data.news)
      isRead.value = false // New message = unread for everyone
      console.log('PlayerNewsModal: Appended status change news (fallback), new count:', playerNews.value.length)
      await nextTick()
      scrollToBottom()
    }
  } catch (error) {
    console.error('Error adding status change news message:', error)
  } finally {
    // Reset flag after a short delay
    setTimeout(() => {
      isAddingNews.value = false
    }, 100)
  }
}

function handleEditorKeydown(event) {
  // Allow Shift+Enter for new line, Enter alone to send
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    addNewsMessage()
  }
}

function handleEditorPaste(event) {
  // Allow default paste behavior - browser handles images, links, and text automatically
  // Update the reactive value after paste and convert URLs to links
  setTimeout(() => {
    convertUrlsToLinks()
    newNewsMessage.value = getMessageText()
    // Also update after a short delay to ensure content is processed
    setTimeout(() => {
      newNewsMessage.value = getMessageText()
    }, 50)
  }, 0)
}

function extractDomain(url) {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname.replace(/^www\./, '') // Remove www. prefix if present
  } catch (e) {
    // Fallback: try to extract domain manually
    const match = url.match(/https?:\/\/(?:www\.)?([^\/]+)/i)
    return match ? match[1] : url
  }
}

function convertUrlsToLinks() {
  if (!messageEditor.value) return
  
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi
  const walker = document.createTreeWalker(
    messageEditor.value,
    NodeFilter.SHOW_TEXT,
    null,
    false
  )
  
  const textNodes = []
  let node
  while (node = walker.nextNode()) {
    // Skip if parent is already a link
    if (node.parentElement && node.parentElement.tagName === 'A') {
      continue
    }
    if (urlRegex.test(node.textContent)) {
      textNodes.push(node)
    }
  }
  
  textNodes.forEach(textNode => {
    const text = textNode.textContent
    const parent = textNode.parentNode
    const parts = []
    let lastIndex = 0
    let match
    
    // Reset regex
    urlRegex.lastIndex = 0
    
    while ((match = urlRegex.exec(text)) !== null) {
      // Add text before URL
      if (match.index > lastIndex) {
        parts.push(document.createTextNode(text.substring(lastIndex, match.index)))
      }
      
      // Create link for URL - keep full URL as text in editor
      const link = document.createElement('a')
      link.href = match[0]
      link.textContent = match[0] // Full URL in editor
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      parts.push(link)
      
      lastIndex = match.index + match[0].length
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(document.createTextNode(text.substring(lastIndex)))
    }
    
    // Replace the text node with the new nodes
    if (parts.length > 0) {
      parts.forEach(part => {
        parent.insertBefore(part, textNode)
      })
      parent.removeChild(textNode)
    }
  })
}

function formatMessageForDisplay(htmlContent) {
  if (!htmlContent) return ''
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContent
  
  // Find all links and update them to show domain name
  const links = tempDiv.querySelectorAll('a')
  links.forEach(link => {
    const href = link.getAttribute('href') || link.href
    if (href) {
      link.textContent = extractDomain(href)
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      // Ensure underline is applied
      link.style.textDecoration = 'underline'
      link.style.color = '#2563eb'
    }
  })
  
  // Also convert plain text URLs to links if they exist
  const textNodes = []
  const walker = document.createTreeWalker(
    tempDiv,
    NodeFilter.SHOW_TEXT,
    null,
    false
  )
  
  let node
  while (node = walker.nextNode()) {
    if (node.parentElement && node.parentElement.tagName !== 'A') {
      const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi
      if (urlRegex.test(node.textContent)) {
        textNodes.push(node)
      }
    }
  }
  
  textNodes.forEach(textNode => {
    const text = textNode.textContent
    const parent = textNode.parentNode
    const parts = []
    let lastIndex = 0
    let match
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi
    
    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(document.createTextNode(text.substring(lastIndex, match.index)))
      }
      
      const link = document.createElement('a')
      link.href = match[0]
      link.textContent = extractDomain(match[0])
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.style.textDecoration = 'underline'
      link.style.color = '#2563eb'
      parts.push(link)
      
      lastIndex = match.index + match[0].length
    }
    
    if (lastIndex < text.length) {
      parts.push(document.createTextNode(text.substring(lastIndex)))
    }
    
    if (parts.length > 0) {
      parts.forEach(part => {
        parent.insertBefore(part, textNode)
      })
      parent.removeChild(textNode)
    }
  })
  
  return tempDiv.innerHTML
}

function handleEditorInput() {
  // Update reactive value immediately for button state
  newNewsMessage.value = getMessageText()
  
  // Convert URLs to links on input (with debounce)
  setTimeout(() => {
    convertUrlsToLinks()
    // Update again after URL conversion
    newNewsMessage.value = getMessageText()
  }, 300)
}

async function addNewsMessage(event) {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  
  const messageContent = getMessageContent()
  const messageText = getMessageText()
  
  if (!messageText || !messageText.trim()) return
  
  // Prevent reloads while adding news
  isAddingNews.value = true
  loading.value = true
  
  try {
    console.log('PlayerNewsModal: Adding news message, current news count:', playerNews.value.length)
    const response = await axios.post(`/api/players/${props.playerId}/news`, {
      message: messageContent, // Send HTML content
      status: selectedStatus.value,
      fixtureId: props.fixtureId || route.params.id
    })
    
    console.log('PlayerNewsModal: News added, response:', response.data)
    
    if (response.data?.success && response.data?.news) {
      // Add the new news to the end of the list (bottom) - don't reload
      const newNewsItem = {
        id: response.data.news.id,
        player_id: response.data.news.player_id,
        fixture_id: response.data.news.fixture_id,
        message: response.data.news.message,
        status: response.data.news.status,
        author: response.data.news.author,
        created_at: response.data.news.created_at,
        timestamp: response.data.news.timestamp || response.data.news.created_at
      }
      playerNews.value.push(newNewsItem)
      isRead.value = false // New message = unread for everyone
      console.log('PlayerNewsModal: Appended news item, new count:', playerNews.value.length)
      
      // Clear editor
      if (messageEditor.value) {
        messageEditor.value.innerHTML = ''
      }
      newNewsMessage.value = ''
      
      // Scroll to bottom after adding new message
      await nextTick()
      scrollToBottom()
    } else if (response.data?.news) {
      // Fallback handling
      playerNews.value.push(response.data.news)
      isRead.value = false // New message = unread for everyone
      console.log('PlayerNewsModal: Appended news item (fallback), new count:', playerNews.value.length)
      if (messageEditor.value) {
        messageEditor.value.innerHTML = ''
      }
      newNewsMessage.value = ''
      await nextTick()
      scrollToBottom()
    }
  } catch (error) {
    console.error('Error adding news message:', error)
  } finally {
    loading.value = false
    // Reset flag after a short delay to allow any pending operations to complete
    setTimeout(() => {
      isAddingNews.value = false
    }, 100)
  }
}

function scrollToBottom() {
  if (newsContainer.value) {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      newsContainer.value.scrollTop = newsContainer.value.scrollHeight
    })
  }
}

async function toggleRead() {
  if (togglingRead.value || !props.playerId) return
  togglingRead.value = true
  try {
    const newReadState = !isRead.value
    const response = await axios.post(`/api/players/${props.playerId}/news/toggle-read`, {
      read: newReadState
    })
    if (response.data?.success) {
      isRead.value = response.data.isRead ?? newReadState
      if (newReadState) {
        playerNews.value = playerNews.value.map(item => ({ ...item, read_at: new Date().toISOString() }))
      } else {
        playerNews.value = playerNews.value.map(item => ({ ...item, read_at: null }))
      }
    }
  } catch (error) {
    console.error('PlayerNewsModal: Error toggling read status:', error)
  } finally {
    togglingRead.value = false
  }
}

function formatTime(timestamp) {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  
  // Format as full date and time: "YYYY-MM-DD HH:MM:SS"
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}
</script>

<template>
  <div v-if="show" 
       class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
       @click.self="closeModal">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[600px] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">
          News & Status - {{ playerName }}
        </h3>
        <button @click="closeModal" 
                class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="flex flex-1 overflow-hidden">
        <!-- Left Side - Status Radio Options -->
        <div class="hidden w-64 border-r border-gray-200 p-4 bg-gray-50">
          <h4 class="text-sm font-semibold text-gray-700 mb-4">Player Status</h4>
          <div class="space-y-2">
            <label @click="updatePlayerStatus"
                   class="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                   :class="selectedStatus === 'active' ? 'bg-green-50 border-2 border-green-200' : 'bg-white border-2 border-transparent hover:bg-gray-100'">
              <input type="radio" 
                     v-model="selectedStatus" 
                     value="active"
                     @change="updatePlayerStatus"
                     class="sr-only">
              <div class="w-4 h-4 rounded-full bg-green-500 flex-shrink-0"></div>
              <span class="text-sm font-medium text-green-700">Active</span>
            </label>

            <label @click="updatePlayerStatus"
                   class="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                   :class="selectedStatus === 'questionable' ? 'bg-orange-50 border-2 border-orange-200' : 'bg-white border-2 border-transparent hover:bg-gray-100'">
              <input type="radio" 
                     v-model="selectedStatus" 
                     value="questionable"
                     @change="updatePlayerStatus"
                     class="sr-only">
              <div class="w-4 h-4 rounded-full bg-orange-500 flex-shrink-0"></div>
              <span class="text-sm font-medium text-orange-700">Questionable</span>
            </label>

            <label @click="updatePlayerStatus"
                   class="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                   :class="selectedStatus === 'out' ? 'bg-red-50 border-2 border-red-200' : 'bg-white border-2 border-transparent hover:bg-gray-100'">
              <input type="radio" 
                     v-model="selectedStatus" 
                     value="out"
                     @change="updatePlayerStatus"
                     class="sr-only">
              <div class="w-4 h-4 rounded-full bg-red-500 flex-shrink-0"></div>
              <span class="text-sm font-medium text-red-700">Out</span>
            </label>
          </div>
        </div>

        <!-- Right Side - News Chat -->
        <div class="flex-1 flex flex-col">
          <!-- News Messages -->
          <div ref="newsContainer" class="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            <div v-if="loading" class="text-center text-gray-500 py-8">
              Loading news...
            </div>
            <div v-else-if="playerNews.length === 0" class="text-center text-gray-500 py-8">
              No news messages yet. Add a message below.
            </div>
            <div v-else v-for="(news, index) in playerNews" 
                 :key="news.id || index"
                 class="flex flex-col gap-1">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-xs font-medium text-gray-600">{{ news.author || 'System' }}</span>
                <span class="text-xs text-gray-400">{{ formatTime(news.created_at || news.timestamp) }}</span>
                <!-- <span v-if="news.status" 
                      class="text-xs px-2 py-0.5 rounded-full capitalize"
                      :class="{
                        'bg-green-100 text-green-700': news.status === 'active',
                        'bg-orange-100 text-orange-700': news.status === 'questionable',
                        'bg-red-100 text-red-700': news.status === 'out'
                      }">
                  {{ news.status }}
                </span> -->
              </div>
              <div 
                class="bg-white rounded-lg p-3 shadow-sm border border-gray-200"
                :class="{ 'border-l-4 border-l-red-500': !news.read_at }"
              >
                <div class="text-sm text-gray-800 prose prose-sm max-w-none" v-html="formatMessageForDisplay(news.message || news.content)"></div>
              </div>
            </div>
          </div>

          <!-- Input Area -->
          <div class="border-t border-gray-200 p-4 bg-white">
            <div class="flex gap-2 items-center flex-wrap">
              <button 
                v-if="playerNews.length > 0"
                @click="toggleRead"
                :disabled="togglingRead"
                type="button"
                class="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                :class="isRead 
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                  : 'bg-red-500 text-white hover:bg-red-600'">
                <svg v-if="isRead" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span v-else class="w-2 h-2 rounded-full bg-white/80"></span>
                {{ isRead ? 'Read' : 'Confirm to be read' }}
              </button>
              <div class="flex-1 min-w-0 flex gap-2 items-end">
              <div 
                ref="messageEditor"
                contenteditable="true"
                @keydown="handleEditorKeydown"
                @paste="handleEditorPaste"
                @input="handleEditorInput"
                @blur="convertUrlsToLinks"
                data-placeholder="Type a news message... (You can paste links and images)"
                class="flex-1 min-h-[60px] max-h-[200px] overflow-y-auto px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm text-gray-800"
                style="word-wrap: break-word; white-space: pre-wrap;"
              ></div>
              <button 
                @click.prevent="addNewsMessage"
                :disabled="!newNewsMessage || !newNewsMessage.trim() || loading"
                class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap">
                Send
              </button>
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-1">Press Enter to send, Shift+Enter for new line</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
[contenteditable][data-placeholder]:empty:before {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
  position: absolute;
}

[contenteditable] {
  position: relative;
}

[contenteditable]:focus {
  outline: none;
}

[contenteditable] a {
  color: #2563eb;
  text-decoration: underline;
  text-underline-offset: 2px;
}

[contenteditable] a:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

[contenteditable] img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 4px 0;
}

.prose a {
  color: #2563eb;
  text-decoration: underline !important;
  text-underline-offset: 2px;
}

.prose a:hover {
  color: #1d4ed8;
  text-decoration: underline !important;
}

/* Ensure all links in message display have underline */
.bg-white a {
  text-decoration: underline !important;
  color: #2563eb;
}

.bg-white a:hover {
  color: #1d4ed8;
  text-decoration: underline !important;
}

.prose img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 4px 0;
}
</style>

